<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent fullscreen scrollable)
  v-card.neutral200
    v-card-text.pt-8.pb-0
      .d-flex.align-center.mb-4
        .ml-4.text-2xl.font-semibold.neutral700--text Select medicine
      v-row.px-4(no-gutters)
        v-col(cols="8")
          v-card.mr-4.border.rounded-lg.pa-6.height-adjustment(outlined)
            template(v-if="isFetching")
              v-skeleton-loader(type="list-item-three-line@6")
            template(v-else)
              .text-sm.mb-2 Tick the checkbox to select item.
              v-text-field.text-sm.round-xl(v-model="itemName" dense placeholder="Type in item name, uom or group name" outlined hide-details="auto" clearable append-icon="mdi-magnify")
                template(v-slot:append)
                  v-icon.small.mr-2 mdi-magnify
              v-data-table.mt-sm-4.pb-6(v-model="selected" :headers="headers" :items="products" item-key="id" :items-per-page="pageSize" :loading="isFetchingProducts" disable-sort hide-default-footer loader-height="2" show-select)
                template(v-slot:header.data-table-select="{ props }")
                  yc-checkbox(:input-value="allSelected" :indeterminate="props.indeterminate" :disabled="products.length === 0 || allDisabled" @change="handleSelectAll")
                template(v-slot:item.data-table-select="{ item, select, isSelected }")
                  yc-checkbox(:input-value="isSelected" :disabled="item.disabled" @change="select($event)")
                  div.d-flex.align-center(v-if="totalCount > pageSize")
                template(v-slot:item.name="{ item }")
                  .py-4
                    .text-sm(:class="[{'neutral500--text': item.hasError}]") {{ item.name }}
                    .text-xxs.danger500--text(v-show="item.hasError") {{ item.errorMessage }}
                template(v-slot:item.stocks="{ item }")
                  .text-sm(:class="[{'danger500--text': item.stocks === 0}]") {{ item.stocks }}
                template(v-slot:item.groups="{ item }")
                  v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                    template(#activator="{ on, attrs }")
                      div(v-bind="attrs" v-on="on" style="max-width: 150px;")
                        .text-sm.neutral700--text.cursor-help.truncate {{ item.groups }}
                    div(style="max-width: 500px;")
                      .text-sm {{ item.groups }}
                template(v-slot:item.price_range="{ item }")
                  template(v-for="(price, idx) in item.price_range")
                    .text-sm(:key="`pr-${idx}`") {{ price | displayPrice($currency) }}
                      span.mx-1(v-if="item.price_range.length > 1 && !idx") -
              div.d-flex.justify-end
                yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")
        v-col
          v-card.border.rounded-lg.pa-6.height-adjustment(outlined)
            v-row(no-gutters)
              v-col
                .text-sm.font-semibold Selected item
              v-spacer
              v-col.d-flex.justify-end(v-if="selected.length > 0")
                v-btn.button-xs.neutral700--text.underline.mr-1(text @click="handleRemoveAllItem") Remove all
            template(v-if="selected.length === 0")
              v-card.px-4.py-8.mt-8.border-dashed(outlined)
                .d-flex.flex-column.align-center.justify-center
                  v-icon.mb-6(size="30") $yzi-colors
                  .text-md.font-semibold.mb-1 No items selected
                  .text-sm Any selected items from your list will show up here.
            template(v-else)
              div(v-for="(selectedItem, selectedItemIdx) in selected" :key="selectedItemIdx")
                v-row.pa-2.align-center(no-gutters)
                  v-col(cols="2")
                    .text-sm {{ selectedItemIdx + 1 }}
                  v-col.mx-2
                    .text-sm {{ selectedItem.name }}
                  v-col.d-flex.justify-end(cols="2")
                    v-btn.button-xs.danger700--text.px-0.underline(text @click="handleRemoveItem(selectedItem)") Remove
                v-divider(v-if="selectedItemIdx !== selected.length - 1")

    v-card-actions.white.pa-0
      .d-flex.flex-column(style="width:100%")
        v-divider
        .d-flex.justify-end.pa-4
          v-btn.button-md.neutral700--text.underline.mr-2(text @click="showDialog = false") Cancel
          v-btn.button-md(:loading="isLoading" color="primary" depressed :disabled="selected.length === 0" @click="handleInsertToTreatmentPlan") Insert to treatment plan
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters } from 'vuex'

export default {
  props: {
    open: {
      type: Boolean
    },
    isOTC: {
      type: Boolean
    },
  },
  data() {
    return {
      isFetching: false,
      isFetchingProducts: false,
      isLoading: false,
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Stock', value: 'stocks'},
        { text: 'UOM', value: 'uom' },
        { text: 'Group', value: 'groups' },
        { text: 'Price', value: 'price_range', width: '120' },
      ],
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      itemName: null,
      products: [],
      selected: [],
    }
  },
  mounted () {
    this.fetchProducts(true)
  },
  watch: {
    currentPage() {
      this.fetchProducts()
    },
    itemName () {
      this.searchStart()
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    allDisabled () {
      return this.products.every(item => item.disabled)
    },
    allSelected () {
      if (this.allDisabled) {
        return false
      }
      const productIds = this.products.filter(item => !item.disabled).map(item => item.id)
      return this.selected.filter(item => productIds.includes(item.id)).length === productIds.length
    }
  },
  methods: {
    pageChanged (page) {
      this.currentPage = page
    },
    searchStart: debounce(function () {
      this.currentPage = 1
      this.fetchProducts()
    }, 750),
    async fetchProducts (firstLoad = false) {
      try {
        if (firstLoad) {
          this.isFetching = true
        }
        this.isFetchingProducts = true
        let params = {
          page: this.currentPage,
          page_size: this.pageSize,
          search: this.itemName || null,
          status: 'published',
          ordering: '-inventory_qty,medication,name'
        }
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/treatment_products`, { params })
        if (data.count > 0) {
          this.products = data.results.map(result => this.sanitizeData(result))
        } else {
          this.products = []
        }
        this.totalCount = data.count
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
        this.isFetchingProducts = false
      }
    },
    hasStock(item) {
      if (!item.inventory_enabled) {
        return true
      }
      if (item.inventory_qty > 0) {
        return true
      }
      return false
    },
    getStock (item) {
      if (!item.inventory_enabled) {
        return null
      }
      return item.inventory_qty
    },
    hasError (item) {
      return item.type === 'documents' || this.getStock(item) === 0
    },
    getErrorMessage (item) {
      if (this.hasError(item)) {
        if (item.type === 'documents') return 'Medical document cannot be added for multiple bulk. Please use single add.'
        if (this.getStock(item) === 0) return 'Out of stock'
      }
      return null
    },
    isDisabled (item) {
      return !this.hasStock(item) || this.hasError(item)
    },
    sanitizeData (item) {
      const groups = item.groups.join(', ')
      return {
        ...item,
        groups,
        stocks: this.getStock(item),
        disabled: this.isDisabled(item),
        hasError: this.hasError(item),
        errorMessage: this.getErrorMessage(item),
      }
    },
    handleSelectAll (value) {
      if (value) {
        this.selected = [...this.selected, ...this.products.filter(item => !this.isDisabled(item))]
      } else {
        const productsId = this.products.map(item => item.id)
        this.selected = this.selected.filter(item => !productsId.includes(item.id))
      }
    },
    handleRemoveItem (item) {
      this.selected = this.selected.filter(selectedItem => selectedItem.id !== item.id)
    },
    handleRemoveAllItem () {
      this.selected = []
    },
    handleInsertToTreatmentPlan () {
      this.isLoading = true
      this.$emit('insert', this.selected)
    }
  }
};
</script>

<style lang="scss" scoped>
.border {
  border: 1px solid var(--v-neutral300) !important;
}
.height-adjustment {
  min-height: 75dvh;
}
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .combostyle .v-input__icon {
    min-width: 22px;
    width: 22px;
    margin-top: 4px;
  }
}
</style>